import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ContentChild,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { PageEvent, TableColumn } from '../../models/irembo-data-table.model';
import { SortEvent } from '../../directives/sortable-header.directive';

@Component({
  standalone: false,
  selector: 'irembogov-data-table',
  templateUrl: './irembo-data-table.component.html',
  styleUrls: ['./irembo-data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboDataTableComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: IremboDataTableComponent,
      multi: true,
    },
  ],
})
export class IremboDataTableComponent
  implements ControlValueAccessor, Validator, OnChanges
{
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() pageSize = 10;
  @Input() currentPage = 1;
  @Input() collectionSize = 0;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 100];
  @Input() asyncData = false;
  @Input() loading = false;
  @Input() searchable = true;
  @Input() selectable = false;
  @Input() minSelection: number | null = null;
  @Input() maxSelection: number | null = null;
  @Input() searchPlaceHolder = 'Search';
  @Input() customTableClass = '';
  @Input() title = '';
  @Input() minimalPagination = false;
  @Input() emptyMessage = 'No records';

  @Output() sortChanged = new EventEmitter<SortEvent>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() selectionChanged = new EventEmitter<any[]>();
  @Output() touched = new EventEmitter<void>();

  @ContentChild(TemplateRef) rowTemplate: TemplateRef<any> | undefined;

  filteredData: any[] = [];
  searchTerm = '';
  selectedItems: any[] = [];
  isTouched = false;
  shimmerTableSize = Array.from(Array(this.pageSize).keys());
  disabled = false;

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
  private _onValidatorChange: () => void = () => {};
  /* eslint-enable */

  writeValue(value: any[]): void {
    if (value && Array.isArray(value)) {
      this.selectedItems = [...value];
      this._onValidatorChange();
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  validate(): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};
    if (this.selectable) {
      if (
        this.minSelection !== null &&
        this.selectedItems.length < this.minSelection
      ) {
        validationErrors['minSelection'] = true;
      }

      if (
        this.maxSelection !== null &&
        this.selectedItems.length > this.maxSelection
      ) {
        validationErrors['maxSelection'] = true;
      }
    }
    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  markAsTouched() {
    if (!this.isTouched) {
      this.isTouched = true;
      this._onTouched();
      this.touched.emit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['minSelection'] ||
      changes['selectable'] ||
      changes['maxSelection']
    ) {
      this._onValidatorChange();
    }
    if (changes['data'] || changes['columns'] || changes['searchTerm']) {
      this.updateInternalState();
    }

    if (changes['pageSize']) {
      const value = changes['pageSize'].currentValue;
      this.shimmerTableSize = Array.from(Array(value > 10 ? 10 : value).keys());
    }
  }

  private updateInternalState(): void {
    if (!this.asyncData) {
      this.filteredData = [...this.data];
      this.collectionSize = this.data.length;
      this.applySearch();
    }
  }

  onSort(event: SortEvent): void {
    if (this.disabled) return;
    this.markAsTouched();
    if (this.asyncData) {
      this.sortChanged.emit(event);
    } else {
      this.sortLocalData(event);
    }
  }

  onPageChange(event: Record<string, number>): void {
    if (this.disabled) return;
    this.markAsTouched();
    this.currentPage = event['pageNumber'];
    if (this.asyncData) {
      this.pageChanged.emit({
        page: this.currentPage,
        pageSize: this.pageSize,
        collectionSize: this.collectionSize,
      });
    }
  }

  handleSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.disabled) return;
    this.searchTerm = target.value;
    this.markAsTouched();
    if (this.asyncData) {
      this.searchChanged.emit(this.searchTerm);
    } else {
      this.applySearch();
    }
  }

  toggleSelection(item: any): void {
    if (this.disabled) return;
    this.markAsTouched();
    if (!this.selectable) return;

    if (this.maxSelection === 1) {
      if (this.selectedItems[0] === item) {
        this.selectedItems = [];
      } else {
        this.selectedItems = [item];
      }
    } else {
      const index = this.selectedItems.findIndex(selected =>
        this.isDeepEqual(selected, item)
      );
      if (index === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(index, 1);
      }
    }

    this.selectionChanged.emit(this.selectedItems);
    this._onChange(this.selectedItems);
    this._onValidatorChange();
  }

  toggleSelectAll(event: Event): void {
    if (this.disabled) return;
    this.markAsTouched();
    if (!this.selectable) return;
    if (this.maxSelection !== null) return;

    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.selectedItems = [...this.currentPageData];
    } else {
      this.selectedItems = [];
    }
    this.selectionChanged.emit(this.selectedItems);
    this._onChange(this.selectedItems);
    this._onValidatorChange();
  }

  isSelected(row: any): boolean {
    return this.selectedItems.some(item => this.isDeepEqual(item, row));
  }

  private isDeepEqual(obj1: any, obj2: any) {
    if (obj1 === obj2) return true;

    if (
      typeof obj1 !== 'object' ||
      obj1 === null ||
      typeof obj2 !== 'object' ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key) || !this.isDeepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }
    return true;
  }

  private applySearch(): void {
    if (!this.searchTerm) {
      this.filteredData = [...this.data];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredData = this.data.filter(item =>
        this.columns.some(column => {
          const value = item[column.key] as string;
          return value?.toString().toLowerCase().includes(term);
        })
      );
    }

    this.collectionSize = this.filteredData.length;
    this.currentPage = 1;
  }

  private sortLocalData(event: SortEvent): void {
    const { column, direction } = event;
    if (!column || direction === '') {
      this.filteredData = [...this.data];
      return;
    }

    this.filteredData.sort((a, b) => {
      const valueA = a[column];
      const valueB = b[column];

      if (valueA == null) return 1;
      if (valueB == null) return -1;
      if (valueA === valueB) return 0;

      return (valueA < valueB ? -1 : 1) * (direction === 'asc' ? 1 : -1);
    });
  }

  get currentPageData(): any[] {
    return this.asyncData
      ? this.data
      : this.filteredData.slice(
          (this.currentPage - 1) * this.pageSize,
          this.currentPage * this.pageSize
        );
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  clearSearch() {
    this.searchTerm = '';
    this.markAsTouched();
    if (this.asyncData) {
      this.searchChanged.emit(this.searchTerm);
    } else {
      this.applySearch();
    }
  }

  getRowContext(row: any) {
    return {
      $implicit: row,
      index: this.currentPageData.indexOf(row),
      columns: this.columns,
      selected: this.isSelected(row),
      toggleSelect: () => this.toggleSelection(row),
      data: this.data,
    };
  }
}
